import React, { useState, useRef, useCallback } from 'react';
import { useClickOutside } from '../hooks/useClickOutside';

const REASON_CHIPS = ['Sick', 'Traveling', 'Work', 'Family', 'No Response'];
const EXCLUSIVE_CHIP = 'No Response';

export function AbsenceReasonPopover({ reason, onSave, onOpenChange }) {
    const [open, setOpen] = useState(false);
    const setOpenAndNotify = (v) => { setOpen(v); onOpenChange?.(v); };
    const [customText, setCustomText] = useState('');
    const popRef = useRef(null);

    // Parse comma-separated reasons into array
    const reasons = reason ? reason.split(', ').filter(Boolean) : [];

    const close = useCallback(() => setOpenAndNotify(false), []);
    useClickOutside(popRef, close);

    const handleOpen = (e) => {
        e.stopPropagation();
        setCustomText('');
        setOpenAndNotify(true);
    };

    const toggleChip = (e, chip) => {
        e.stopPropagation();
        if (reasons.includes(chip)) {
            onSave(reasons.filter((r) => r !== chip).join(', '));
        } else if (chip === EXCLUSIVE_CHIP) {
            onSave(EXCLUSIVE_CHIP);
        } else {
            onSave([...reasons.filter((r) => r !== EXCLUSIVE_CHIP), chip].join(', '));
        }
    };

    const removeReason = (e, r) => {
        e.stopPropagation();
        onSave(reasons.filter((x) => x !== r).join(', '));
    };

    const addCustom = (e) => {
        e.stopPropagation();
        const trimmed = customText.trim();
        if (!trimmed || reasons.includes(trimmed)) return;
        onSave([...reasons.filter((r) => r !== EXCLUSIVE_CHIP), trimmed].join(', '));
        setCustomText('');
    };

    // Closed state: show reason badges + "+ reason" badge
    if (!open) {
        return (
            <div className="flex flex-wrap gap-1 justify-center mt-1">
                {reasons.map((r) => (
                    <span
                        key={r}
                        className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700 leading-tight"
                    >
                        {r}
                    </span>
                ))}
                <button
                    className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-400 dark:border-slate-600 dark:hover:bg-slate-600 leading-tight transition-colors"
                    onClick={handleOpen}
                >
                    +
                </button>
            </div>
        );
    }

    // Open state: popover with instant-save chips
    const availableChips = REASON_CHIPS.filter((c) => !reasons.includes(c));
    const customReasons = reasons.filter((r) => !REASON_CHIPS.includes(r));

    return (
        <div ref={popRef} className="absolute z-20 left-1/2 -translate-x-1/2 mt-1 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-2.5" onClick={(e) => e.stopPropagation()}>
            {/* Active reasons — click to remove */}
            {reasons.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                    {reasons.map((r) => (
                        <button
                            key={r}
                            className="inline-flex items-center gap-0.5 text-[11px] px-2 py-0.5 rounded-full bg-red-100 border border-red-300 text-red-700 dark:bg-red-900/40 dark:border-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
                            onClick={(e) => removeReason(e, r)}
                            title={`Remove "${r}"`}
                        >
                            {r}
                            <span className="text-red-400 dark:text-red-500 ml-0.5">&times;</span>
                        </button>
                    ))}
                </div>
            )}
            {/* Available preset chips — click to add */}
            {availableChips.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                    {availableChips.map((chip) => (
                        <button
                            key={chip}
                            className="text-[11px] px-2 py-0.5 rounded-full border bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-600 transition-colors"
                            onClick={(e) => toggleChip(e, chip)}
                        >
                            {chip}
                        </button>
                    ))}
                </div>
            )}
            {/* Custom reason input with + button */}
            <div className="flex gap-1">
                <input
                    type="text"
                    className="flex-1 min-w-0 text-xs border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-accent-500 placeholder:text-slate-400"
                    placeholder="Custom reason..."
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') addCustom(e); }}
                    autoFocus
                />
                <button
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500 text-white hover:bg-accent-600 transition-colors flex-shrink-0 text-sm font-bold"
                    onClick={addCustom}
                    disabled={!customText.trim()}
                >
                    +
                </button>
            </div>
        </div>
    );
}
